import makerjs from 'makerjs';

function createModelForArrow( primitives ) {
	const difference = primitives.stem_width - primitives.head_width;

	return new makerjs.models.ConnectTheDots(
		true, // Closed,
		[
			[ 0, 0 ],
			[ primitives.stem_length, 0 ],
			[ primitives.stem_length, difference / 2 ],
			[ primitives.stem_length + primitives.head_length, primitives.stem_width / 2 ],
			[ primitives.stem_length, primitives.stem_width - ( difference / 2 ) ],
			[ primitives.stem_length, primitives.stem_width ],
			[ 0, primitives.stem_width ],
		]
	);
}

function createModelForHeart( { height } ) {
	const a = height / ( ( 3 / 4 * Math.sqrt( 2 ) ) + ( 1 / 2 ) );

	const diagonal = a * Math.sqrt( 2 );
	const halfDiagonal = diagonal / 2;

	return {
		paths: {
			right_line: new makerjs.paths.Line( [ 0, -1 * halfDiagonal ], [ halfDiagonal, 0 ] ),
			left_line: new makerjs.paths.Line( [ 0, -1 * halfDiagonal ], [ -1 * halfDiagonal, 0 ] ),
			right_arc: new makerjs.paths.Arc( [ halfDiagonal / 2, halfDiagonal / 2 ], a / 2, -45, 135 ),
			left_arc: new makerjs.paths.Arc( [ -1 * halfDiagonal / 2, halfDiagonal / 2 ], a / 2, 45, 225 ),
		},
		origin: [ 0, 0 ],
	};
}

function createModelForArchedRectangle( dimensions ) {
	const height = Math.max( dimensions.height - ( dimensions.width / 2 ), 0 );
	return {
		paths: {
			bottom_line: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.width, 0 ] ),
			left_line: new makerjs.paths.Line( [ 0, 0 ], [ 0, height ] ),
			right_line: new makerjs.paths.Line( [ dimensions.width, 0 ], [ dimensions.width, height ] ),
			arc: new makerjs.paths.Arc(
				[ dimensions.width / 2, height ],
				dimensions.width / 2,
				0,
				180
			),
		},
		origin: [ 0, 0 ],
	};
}

function createModelForRoundedRectangle( dimensions ) {
	const useIndividualCorners = dimensions[ 'use_individual_corners' ] === 'true' || dimensions[ 'use_individual_corners' ] === true;

	// Invert length and width to properly render the shape.
	const { length: width, width: length, top_right_radius, bottom_right_radius, bottom_left_radius, top_left_radius, radius } = dimensions;

	const maxRadius = Math.min( length, width ) / 2;

	const topRightRadius = Math.min( useIndividualCorners ? top_right_radius : radius, maxRadius );
	const bottomRightRadius = Math.min( useIndividualCorners ? bottom_right_radius : radius, maxRadius );
	const bottomLeftRadius = Math.min( useIndividualCorners ? bottom_left_radius : radius, maxRadius );
	const topLeftRadius = Math.min( useIndividualCorners ? top_left_radius : radius, maxRadius );

	return {
		paths: {
			BottomLeft: new makerjs.paths.Arc( [bottomLeftRadius, bottomLeftRadius], bottomLeftRadius, 180, 270 ),
			BottomRight: new makerjs.paths.Arc( [width - bottomRightRadius, bottomRightRadius], bottomRightRadius, 270, 0 ),
			TopRight: new makerjs.paths.Arc( [width - topRightRadius, length - topRightRadius], topRightRadius, 0, 90 ),
			TopLeft: new makerjs.paths.Arc( [topLeftRadius, length - topLeftRadius], topLeftRadius, 90, 180 ),
			Bottom: new makerjs.paths.Line( [bottomLeftRadius, 0], [width - bottomRightRadius, 0] ),
			Top: new makerjs.paths.Line( [width - topRightRadius, length], [topLeftRadius, length] ),
			Right: new makerjs.paths.Line( [width, bottomRightRadius], [width, length - topRightRadius] ),
			Left: new makerjs.paths.Line( [0, length - topLeftRadius], [0, bottomLeftRadius] )
		},
		origin: [ 0, 0 ],
	};
}

function createModelForFlatOval( width, radius ) {
	const model = new makerjs.models.Belt(
		radius,
		width - ( radius * 2 ),
		radius
	);

	makerjs.model.originate( model );
	makerjs.model.zero( model );

	return model;
}

function createModelForOctagon( sideLength ) {
	const sides = 8;
	const angleInRadians = Math.PI / sides;
	const sinAngle = Math.sin( angleInRadians );

	// Calculate the radius of the octagon based on the side length.
	const radius = sideLength / ( 2 * sinAngle );
	return new makerjs.models.Polygon(
		sides,
		radius,
		22.5 // Rotate the octagon by 22.5 degrees to make it symmetrical.
	);
}

// See app-measurements-selector/docs/droplet.js for more information.
function createModelForDroplet( height, width ) {
	const xAxisMiddle = width / 2;

	/**
	 * The base_circle is a circle that is as wide as the total with of the droplet, and touches the bottom of the
	 * border. This base_circle is used to calculate the eventual arc of the droplet_base.
	 */
	const baseCircleRadius = xAxisMiddle;
	const baseCircleOrigin = [baseCircleRadius, baseCircleRadius];
	const baseCircle = new makerjs.paths.Circle( baseCircleOrigin, baseCircleRadius );

	/**
	 * The droplet_tip is an iscoceles triangle. This triangle has a top point that is at the top of the border in the
	 * middle. The base of the triangle starts at an exact point where the angled line of the triangle and the arc of
	 * the circle connect. To calculate this point, we need an imaginary instersect_circle that intersects with the
	 * base_circle. This intersect_circle is touches the origin point of the base_circle, and the top of the border.
	 *
	 * Since the origin point of a circle is in it's middle, we have to calculate the radius of the circle first, and
	 * then add that radius to the Y axis of the origin point of the base_circle.
	 */
	const intersectCircleRadius = ( height - xAxisMiddle ) / 2;
	const intersectCircleOrigin = [baseCircleOrigin[ 0 ], baseCircleOrigin[ 1 ] + intersectCircleRadius];
	const intersectCircle = new makerjs.paths.Circle( intersectCircleOrigin, intersectCircleRadius );

	/**
	 * Calculate the intersection_point_a and intersection_point_b, which are the points where the base of the
	 * droplet_tip will start.
	 */
	const intersectionPoints = makerjs.path.intersection( intersectCircle, baseCircle );

	if( ! intersectionPoints ) {
		return {
			paths: {
				baseCircle: baseCircle,
			}
		};
	}

	const intersectionPointA = intersectionPoints.intersectionPoints[ 0 ];
	const intersectionPointB = intersectionPoints.intersectionPoints[ 1 ];

	/**
	 * The droplet_tip is an iscoceles triangle. This triangle has a top point that is at the top of the border in the
	 * middle, and connects to intersection_point_a and intersection_point_b.
	 */
	const dropletTipTop = [xAxisMiddle, height];
	const dropletTipLineA = new makerjs.paths.Line( intersectionPointA, dropletTipTop );
	const dropletTipLineB = new makerjs.paths.Line( intersectionPointB, dropletTipTop );

	/**
	 * Draw the base of the circle by drawing an Arc that has the same origin and radius as the base_circle, and the
	 * angles that are calculated by the intersection.
	 */
	const dropletBase = new makerjs.paths.Arc( baseCircleOrigin, baseCircleRadius, intersectionPoints.path2Angles[ 1 ], intersectionPoints.path2Angles[ 0 ] );

	return {
		paths : {
			dropletTipLineA: dropletTipLineA,
			dropletTipLineB: dropletTipLineB,
			dropletBase: dropletBase
		}
	};
}

function createModelForEgg( height, width ) {
	const startAngle = 0;
	const endAngle = 180;
	const radiusY = height / 3;
	const radiusX = width / 2;

	const arcTop = new makerjs.models.EllipticArc( startAngle, endAngle, radiusX, radiusY * 2 );
	const arcBottom = new makerjs.models.EllipticArc( startAngle, endAngle, radiusX, radiusY );

	const model = {
		models: {
			arcTop: arcTop,
			arcBottom: makerjs.model.rotate( arcBottom, -180 ),
		}
	};

	return model;
}

function createModelForBarrel( width, height, sideHeight ) {
	let arcHeight = Math.max( 0, ( height - sideHeight ) / 2 );

	// Make sure the makerjs shape can't fail by having a 0 height arc.
	if( arcHeight === 0 ) {
		arcHeight = 5;
	}

	return {
		paths: {
			lineLeft: new makerjs.paths.Line( [0, 0], [0, sideHeight] ),
			lineRight: new makerjs.paths.Line( [width, 0], [width, sideHeight] ),
			arcTop: new makerjs.paths.Arc( [0, sideHeight], [width/2, arcHeight + sideHeight], [width, sideHeight] ),
			arcBottom: new makerjs.paths.Arc( [0, 0], [width/2, -arcHeight], [width, 0] )
		}
	};
}

function createModelForParallelogram( height, width, flipHorizontal = false ) {
	const angle = 60; // Angle in degrees

	// Calculate the x-offset for the slanted side
	const offset = height / Math.tan( makerjs.angle.toRadians( angle ) );

	if( flipHorizontal ) {
		return new makerjs.models.ConnectTheDots( true, [
			[offset, 0], // Bottom left corner
			[width + offset, 0], // Bottom right corner
			[width, height], // Top left corner
			[ 0, height ], // Top right corner
		] );
	}

	// Create a new model for the parallelogram
	return new makerjs.models.ConnectTheDots( true, [
		[0, 0], // Bottom left corner
		[width, 0], // Bottom right corner
		[width + offset, height], // Top right corner
		[offset, height] // Top left corner
	] );
}

export function createModelFromShape( shape, dimensions ) {
	const offset_top = ( shape === 'trapezoid' && ( dimensions.width_bottom > dimensions.width_top ) ) ? ( dimensions.width_bottom - dimensions.width_top ) / 2 : 0;
	const offset_bottom = ( shape === 'trapezoid' && ( dimensions.width_top > dimensions.width_bottom ) ) ? ( dimensions.width_top - dimensions.width_bottom ) / 2 : 0;

	switch ( shape ) {
		default:
		case 'rectangle':
			return new makerjs.models.Rectangle(
				dimensions.length,
				dimensions.width
			);
		case 'circle':
			return {
				paths: {
					circle: {
						type: 'circle',
						origin: [ 0, 0 ],
						radius: dimensions.diameter / 2,
					},
				},
			};
		case 'right-angled-triangle':
			return {
				paths: {
					a: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.base, 0 ] ),
					b: new makerjs.paths.Line( [ dimensions.base, 0 ], [ dimensions.base, dimensions.height ] ),
					c: new makerjs.paths.Line( [ dimensions.base, dimensions.height ], [ 0, 0 ] ),
				},
			};
		case 'right-angled-triangle-mirrored':
			return {
				paths: {
					a: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.base, 0 ] ),
					b: new makerjs.paths.Line( [ dimensions.base, 0 ], [ 0, dimensions.height ] ),
					c: new makerjs.paths.Line( [ 0, dimensions.height ], [ 0, 0 ] ),
				},
			};
		case 'arched-rectangle':
			return createModelForArchedRectangle( dimensions );
		case 'rounded-rectangle':
			return createModelForRoundedRectangle( dimensions );
		case 'egg':
			return createModelForEgg( dimensions.height, dimensions.width );
		case 'ellipse':
			return new makerjs.models.Ellipse(
				dimensions.width / 2,
				dimensions.height / 2
			);
		case 'flat-oval':
			return createModelForFlatOval( dimensions.width, dimensions.radius );
		case 'isosceles-triangle':
			return {
				paths: {
					a: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.base, 0 ] ),
					b: new makerjs.paths.Line( [ dimensions.base, 0 ], [ dimensions.base / 2, dimensions.height ] ),
					c: new makerjs.paths.Line( [ dimensions.base / 2, dimensions.height ], [ 0, 0 ] ),
				},
			};
		case 'arrow':
			return createModelForArrow( dimensions );
		case 'half-circle':
			return {
				paths: {
					base: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.diameter, 0 ] ),
					arc: new makerjs.paths.Arc(
						[ dimensions.diameter / 2, 0 ],
						dimensions.diameter / 2,
						0,
						180
					),
				},
			};
		case 'quarter-circle':
			return {
				paths: {
					base: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.radius, 0 ] ),
					height: new makerjs.paths.Line( [ 0, 0 ], [ 0, dimensions.radius ] ),
					arc: new makerjs.paths.Arc(
						[ 0, 0 ],
						dimensions.radius,
						0,
						90
					),
				},
			};
		case 'hexagon':
			return new makerjs.models.Polygon(
				6,
				dimensions.side_length
			);
		case 'polygon':
			return new makerjs.models.Polygon(
				( dimensions.side_amount ?? 8 ) < 3 ? 3 : dimensions.side_amount,
				dimensions.width / 2
			);
		case 'octagon':
			return createModelForOctagon( dimensions.side_length );
		case 'star':
			return new makerjs.models.Star(
				dimensions.number_of_points,
				dimensions.outer_radius,
				dimensions.inner_radius,
				0
			);
		case 'sloped-rectangle-flip-horizontal':
		case 'sloped-rectangle':
			return {
				paths: {
					width: new makerjs.paths.Line( [ 0, 0 ], [ dimensions.width, 0 ] ),
					right_height: new makerjs.paths.Line(
						[ dimensions.width, 0 ],
						[ dimensions.width, dimensions.height_right ]
					),
					diagonal: new makerjs.paths.Line(
						[ dimensions.width, dimensions.height_right ],
						[ 0, dimensions.height_left ]
					),
					left_height: new makerjs.paths.Line(
						[ 0, dimensions.height_left ],
						[ 0, 0 ]
					),
				},
			};
		case 'sloped-right-rectangle-flip-vertical':
		case 'sloped-right-rectangle':
			return {
				paths: {
					width_top: new makerjs.paths.Line( [ 0, dimensions.height ], [ dimensions.width_top, dimensions.height ] ),
					diagonal: new makerjs.paths.Line( [ dimensions.width_top, dimensions.height ], [ dimensions.width_bottom, 0 ] ),
					width_bottom: new makerjs.paths.Line( [ dimensions.width_bottom, 0 ], [ 0, 0 ] ),
					height: new makerjs.paths.Line( [ 0, 0 ], [ 0, dimensions.height ] ),
				},
			};
		case 'trapezoid':
			return {
				paths: {
					width_bottom: new makerjs.paths.Line( [ offset_bottom, 0 ], [ dimensions.width_bottom + offset_bottom, 0 ] ),
					right_side: new makerjs.paths.Line(
						[ dimensions.width_bottom + offset_bottom, 0 ],
						[ dimensions.width_top + offset_top, dimensions.height ]
					),
					width_top: new makerjs.paths.Line(
						[ offset_top, dimensions.height ],
						[ dimensions.width_top + offset_top, dimensions.height ]
					),
					left_side: new makerjs.paths.Line(
						[ offset_top, dimensions.height ],
						[ offset_bottom, 0 ]
					),
				},
			};
		case 'heart':
			return createModelForHeart( dimensions );
		case 'kite':
			return {
				paths: {
					topLeft: new makerjs.paths.Line( [-dimensions.width/2, 0], [0, dimensions.height_top] ),
					topRight: new makerjs.paths.Line( [dimensions.width/2, 0], [0, dimensions.height_top] ),
					bottomLeft: new makerjs.paths.Line( [-dimensions.width/2, 0], [0, -dimensions.height_bottom] ),
					bottomRight: new makerjs.paths.Line( [dimensions.width/2, 0], [0, -dimensions.height_bottom] ),
				}
			};
		case 'barrel':
			return createModelForBarrel( dimensions.width, dimensions.height, dimensions.side_height );
		case 'jewel':
			return {
				paths: {
					topBase: new makerjs.paths.Line( [-dimensions.width_top/2, dimensions.height_top], [dimensions.width_top/2, dimensions.height_top] ),
					topLeft: new makerjs.paths.Line( [-dimensions.width_bottom/2, 0], [-dimensions.width_top/2, dimensions.height_top] ),
					topRight: new makerjs.paths.Line( [dimensions.width_top/2, dimensions.height_top], [dimensions.width_bottom/2, 0] ),
					bottomLeft: new makerjs.paths.Line( [-dimensions.width_bottom/2, 0], [0, -dimensions.height_bottom] ),
					bottomRight: new makerjs.paths.Line( [dimensions.width_bottom/2, 0], [0, -dimensions.height_bottom] )
				}
			};
		case 'droplet':
			return createModelForDroplet( dimensions.height, dimensions.width );
		case 'parallelogram-flip-horizontal':
			return createModelForParallelogram( dimensions.height, dimensions.width, true );
		case 'parallelogram':
			return createModelForParallelogram( dimensions.height, dimensions.width );
	}

}

// Re-export makerjs, so we can load this file as a dynamic import and include MakerJS in that partial bundle.
export { makerjs };
